/**
 * The words for a scan that ended with no page kept. Pure, so the scan fault harness
 * (tests/scan-sheets-tests) checks it against the scanner host's own sentences.
 */
export type ScanProblem = { title: string; steps: string[]; detail?: string };

/** A scan fault as a code word (telemetry catalog `scanner.fault`): never the scanner's name or its words. */
export type ScannerFault =
  | 'feeder-empty'
  | 'jam'
  | 'double-feed'
  | 'cover-open'
  | 'off'
  | 'in-use'
  | 'did-not-start'
  | 'driver-stopped'
  | 'page-not-saved'
  | 'twain-missing'
  | 'busy'
  | 'other';

/** The scanner host's own first sentences (services/scanner-host/ScanOutcomes.cs), each with its code. */
const hostFaults: ReadonlyArray<readonly [string, ScannerFault]> = [
  ['The feeder is empty.', 'feeder-empty'],
  ['The paper jammed.', 'jam'],
  ['Two sheets went through together.', 'double-feed'],
  ["The scanner's cover is open.", 'cover-open'],
  ['The scanner is off or unplugged.', 'off'],
  ['The scanner is in use by another program.', 'in-use'],
  ['The scanner did not start.', 'did-not-start'],
  ['The scanner driver stopped unexpectedly.', 'driver-stopped'],
  ['A scanned page could not be saved.', 'page-not-saved'],
  ['The scanner reported a problem.', 'other'],
  ['The TWAIN scanner software could not be loaded on this computer.', 'twain-missing'],
  ['Another scan is running.', 'busy'],
];

/** Which fault a scan ended with, from the same words `scanProblemFor` reads. */
export function scanFaultFor(message: string | null): ScannerFault {
  const text = (message ?? '').trim();
  const own = hostFaults.find(([sentence]) => text.startsWith(sentence));
  if (own) return own[1];
  if (/could not open scanner/i.test(text)) return 'off';
  if (/transfer error|timed out|stopped responding|closed without/i.test(text))
    return 'driver-stopped';
  return 'other';
}

/** The scanner host's refusal, said the way an operator can act on it. */
export function scanProblemFor(sourceName: string, message: string | null): ScanProblem {
  // The scanner host names each fault in two plain sentences: what happened, then what to do
  // (services/scanner-host/ScanOutcomes.cs). Those words are the problem; the generic advice
  // below would contradict them ("has paper loaded" over a double feed). UI fix, for Brandy's review.
  const own = message?.match(
    /^(The feeder is empty\.|The paper jammed\.|Two sheets went through together\.|The scanner's cover is open\.|The scanner is off or unplugged\.|The scanner is in use by another program\.|The scanner did not start\.|The scanner driver stopped unexpectedly\.|A scanned page could not be saved\.|The scanner reported a problem\.|The TWAIN scanner software could not be loaded on this computer\.|Another scan is running\.)\s*(.*)$/,
  );
  if (own)
    return {
      title: `${sourceName}: ${own[1]}`,
      steps: own[2] ? [own[2]] : ['Check the scanner and scan again.'],
    };
  if (message && /transfer error|timed out|stopped responding|closed without/i.test(message))
    return {
      title: `${sourceName} stopped before any page was scanned.`,
      steps: [
        'Clear any jammed paper and check the scanner is still connected.',
        'Put the paper back in the feeder and scan again.',
      ],
      detail: message,
    };
  if (message && /could not open scanner/i.test(message))
    return {
      title: `${sourceName} isn't responding.`,
      steps: [
        'Make sure the scanner is turned on and finished starting up.',
        'Check that its USB cable is connected to this computer.',
        'Close any other program that is using the scanner.',
      ],
    };
  return {
    title: `${sourceName} could not start scanning.`,
    steps: ['Check that the scanner is on, connected, and has paper loaded.'],
    detail: message ?? undefined,
  };
}
